/**
 * The 2 px reading-progress bar (`body > .om-progress > .om-progress__bar`), mounted only while the
 * page scrolls more than 2 600 px and never under reduced motion. Mount once in the shell. Also sets
 * `<html data-omega-motion="on">` while motion is allowed, as production did.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isLongEnough, progressWidth, scrollSpan } from './progressMath';

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => {
      setReduced(mq.matches);
    };
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);
  return reduced;
}

export function ReadingProgress() {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reduced) {
      setMounted(false);
      return;
    }
    const root = document.documentElement;
    root.setAttribute('data-omega-motion', 'on');
    let ticking = 0;
    const update = (): void => {
      const span = scrollSpan(root.scrollHeight, window.innerHeight);
      const long = isLongEnough(span);
      setMounted(long);
      const bar = barRef.current;
      if (long && bar !== null) {
        bar.style.width = progressWidth(window.pageYOffset || root.scrollTop || 0, span);
      }
    };
    const onFrame = (): void => {
      if (ticking) return;
      ticking = requestAnimationFrame(() => {
        ticking = 0;
        update();
      });
    };
    window.addEventListener('scroll', onFrame, { passive: true });
    window.addEventListener('resize', onFrame, { passive: true });
    // Page height changes as routes and late content (product grids, images) arrive.
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(onFrame) : null;
    resize?.observe(document.body);
    update();
    return () => {
      cancelAnimationFrame(ticking);
      window.removeEventListener('scroll', onFrame);
      window.removeEventListener('resize', onFrame);
      resize?.disconnect();
      root.removeAttribute('data-omega-motion');
    };
  }, [reduced]);

  // The bar's width is written on the frame it mounts, before the browser paints it.
  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    const bar = barRef.current;
    if (bar !== null) {
      bar.style.width = progressWidth(
        window.pageYOffset || root.scrollTop || 0,
        scrollSpan(root.scrollHeight, window.innerHeight),
      );
    }
  }, [mounted]);

  if (reduced || !mounted) return null;
  return createPortal(
    <div className="om-progress">
      <div className="om-progress__bar" ref={barRef} />
    </div>,
    document.body,
  );
}
